import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";

import { AppModel } from "../../../app/app-model";
import { NameFields } from "../../../components/name-fields";
import { Button } from "../../../components/ui-kit/button";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { TagsInput } from "../../../components/ui-kit/fields/tags-input";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormField } from "../../../components/ui-kit/form/form-field";
import { FormGrid } from "../../../components/ui-kit/form/form-grid";
import { managingRoles } from "../../../constants/constants";
import { maybeAsArray } from "../../../utils/utils";
import { CountrySelect } from "../../patient/country-select";
import { RegionSelect } from "../../patient/region-select";
import { ClinicsModal } from "../common/clinics-modal";
import { PatientsModal } from "../common/patients-modal";
import { UsersModal } from "../common/users-modal";
import { AddPhysician } from "./add-physician";

export const AddPhysicianForm = observer(function AddPhysicianForm(props: {
  app: AppModel;
  model: AddPhysician;
  onSuccess: (uuid: string) => void;
  onCancel?: () => void;
  compact?: boolean;
}) {
  const { app, model, onSuccess, onCancel, compact } = props;
  const { t } = useLingui();

  return (
    <Form
      onSubmit={async () => {
        const uuid = await model.post(model.addingList.phone);
        if (uuid != null) {
          onSuccess(uuid);
        }
      }}
    >
      <div className="flex max-w-xl flex-col gap-4">
        <NameFields model={model.addingList} />

        <FormGrid>
          <FormField label={t`Country`} mandatory>
            <CountrySelect
              attr="country"
              regionAttr="region"
              model={model.addingList}
            />
          </FormField>

          <FormField label={t`Region`} mandatory>
            <RegionSelect
              attr="region"
              country={model.addingList.country}
              model={model.addingList}
            />
          </FormField>

          <FormField label={t`Email`} mandatory>
            <TextInput attr="email" model={model.addingList} />
          </FormField>

          <FormField label={t`Phone(s)`} mandatory>
            <TagsInput attr="phone" model={model.addingList} />
          </FormField>
        </FormGrid>

        {!compact && (
          <>
            <div className="border-border border-t" />

            <div className="flex gap-6 *:flex-1">
              <FormField label={t`Connected user`}>
                <ChooseField
                  label={
                    model.addingList["connected_user_uuid"]
                      ? app.adminShit?.users?.userMapping[
                          model.addingList["connected_user_uuid"]
                        ]
                      : t`None`
                  }
                  modal={({ onClose }) => {
                    const users = app.requireAdminShit().createUsers();
                    return (
                      <UsersModal
                        title={t`Choose connected user for a new physician`}
                        preselected={maybeAsArray(
                          model.addingList["connected_user_uuid"],
                        )}
                        filter={{ role: ["study-physician", "physician"] }}
                        mode="single"
                        model={users}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          model.addingList["connected_user_uuid"] = selected[0];
                        }}
                      />
                    );
                  }}
                />
              </FormField>

              <FormField label={t`Clinics`}>
                <ChooseField
                  count={model.addingList["clinics_uuids"]?.length ?? 0}
                  modal={({ onClose }) => {
                    const clinics = app.requireAdminShit().createClinics();
                    return (
                      <ClinicsModal
                        preselected={model.addingList["clinics_uuids"]}
                        filter={{ country: [model.addingList.country] }}
                        mode="multi"
                        model={clinics}
                        title={t`Choose clinics for a new physician`}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          model.addingList["clinics_uuids"] = selected;
                        }}
                      />
                    );
                  }}
                />
              </FormField>

              <FormField label={t`Patients`}>
                <ChooseField
                  count={model.addingList["patient_ids"]?.length ?? 0}
                  modal={({ onClose }) => {
                    const patients = app.requireAdminShit().createPatients();
                    return (
                      <PatientsModal
                        title={t`Choose patients for a new physician`}
                        model={patients}
                        preselected={model.addingList["patient_ids"]}
                        filter={{ country: [model.addingList.country] }}
                        mode="multi"
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          model.addingList["patient_ids"] = selected;
                        }}
                      />
                    );
                  }}
                />
              </FormField>

              <FormField label={t`Monitoring user(s)`}>
                <ChooseField
                  count={model.addingList["assigned_users_uuids"]?.length ?? 0}
                  modal={({ onClose }) => {
                    const users = app
                      .requireAdminShit()
                      .createMonitoringUsers(
                        model.addingList.country,
                        model.addingList.region,
                      );
                    return (
                      <UsersModal
                        title={t`Choose monitoring user(s) for a new physician`}
                        preselected={model.addingList["assigned_users_uuids"]}
                        filter={{ role: managingRoles }}
                        mode="multi"
                        model={users}
                        onClose={onClose}
                        onConfirm={({ selected }) => {
                          model.addingList["assigned_users_uuids"] = selected;
                        }}
                      />
                    );
                  }}
                />
              </FormField>
            </div>
          </>
        )}
      </div>

      <div className="mt-6 flex gap-2">
        <Button type="submit">
          <Trans>Add physician</Trans>
        </Button>
        {onCancel && (
          <Button variant="subtle" type="button" onClick={onCancel}>
            <Trans>Cancel</Trans>
          </Button>
        )}
      </div>
    </Form>
  );
});
